/**
 * Platform Admin → Errors, made readable (Frank, 1 Nov 2026: "nothing detailed —
 * where it fails, which user, what platform… I just see a generic 'connection
 * lost while in flight'"). The rules that turn a stored error row into what a
 * person can act on: what kind of problem it is, which ones are the same
 * problem, the device in words, what the connection was doing, and what a
 * failed payment means and who has to fix it.
 *
 * Pure — no Convex imports. Unit-tested in errorInsights.test.ts.
 */

export type ErrorCategory = "payment" | "whatsapp" | "ai" | "page" | "network" | "app";

export const CATEGORY_LABELS: Record<ErrorCategory, string> = {
  payment: "Payments",
  whatsapp: "WhatsApp",
  ai: "AI",
  page: "Page crashed",
  network: "Connection drop",
  app: "App error",
};

/**
 * The phone or browser lost its link to our server mid-call — screen locked,
 * switched apps, weak signal. Not a fault in our code; the call is retried or
 * the user tries again.
 */
export function isConnectionDrop(message: string | null | undefined): boolean {
  return /connection lost while (action|mutation|query) was in flight|websocket (closed|disconnected)|failed to fetch|networkerror when attempting|network request failed|^load failed/i.test(
    String(message || "").trim(),
  );
}

/** What kind of problem a row is, from where it came from and what it says. */
export function classifyError(source: string, message: string): ErrorCategory {
  if (isConnectionDrop(message)) return "network";
  const s = String(source || "");
  if (/^payments?\//i.test(s) || /flutterwave/i.test(s)) return "payment";
  if (s.startsWith("page:")) return "page";
  if (/whatsapp|wasender/i.test(s)) return "whatsapp";
  if (/^ai\/|imageTransformation|imageQuickCheck|productImageAI|modelRegeneration|mockupGeneration|videoGeneration|flyerGeneration|vertex|gemini|assistant/i.test(s)) return "ai";
  return "app";
}

/**
 * Rows that are the same problem share this: the function, and the message
 * with the parts that change every time (request ids, long numbers, ids,
 * phone numbers, order numbers) folded away.
 */
export function errorFingerprint(source: string, message: string): string {
  const folded = String(message || "")
    .replace(/\(request [a-z0-9]+\)/gi, "")
    .replace(/request id:?\s*[a-z0-9_-]+/gi, "")
    .replace(/\b[A-Z]{2,5}-\d{8}-\d{3,6}\b/g, "#order")
    .replace(/\+?\d[\d\s-]{6,}\d/g, "#n")
    .replace(/\b[a-z0-9]{24,}\b/gi, "#id")
    .replace(/\b\d{3,}\b/g, "#")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160)
    .toLowerCase();
  return `${source}|${folded}`;
}

export type DeviceInfo = { device: string; os: string; browser: string; label: string };

/** "iPhone · iOS 18.7 · Safari" from a browser's user agent. */
export function deviceFromUserAgent(ua: string | null | undefined): DeviceInfo {
  const s = String(ua || "");
  let device = "Unknown device";
  let os = "";
  let browser = "";

  const ios = /(iPhone|iPad|iPod).*?OS (\d+)[_.](\d+)/.exec(s);
  const android = /Android (\d+(?:\.\d+)?)/.exec(s);
  if (ios) {
    device = ios[1] === "iPod" ? "iPod" : ios[1];
    os = `iOS ${ios[2]}.${ios[3]}`;
  } else if (android) {
    device = /Mobile/.test(s) ? "Android phone" : "Android tablet";
    os = `Android ${android[1]}`;
  } else if (/Windows NT/.test(s)) {
    device = "Windows computer";
    os = "Windows";
  } else if (/Macintosh|Mac OS X/.test(s)) {
    device = "Mac";
    os = "macOS";
  } else if (/CrOS/.test(s)) {
    device = "Chromebook";
    os = "ChromeOS";
  } else if (/Linux/.test(s)) {
    device = "Linux computer";
    os = "Linux";
  }

  const v = (re: RegExp) => re.exec(s)?.[1];
  if (/FBAN|FBAV|FB_IAB/.test(s)) browser = "Facebook app";
  else if (/Instagram/.test(s)) browser = "Instagram app";
  else if (/WhatsApp/i.test(s)) browser = "WhatsApp app";
  else if (v(/SamsungBrowser\/(\d+)/)) browser = `Samsung Internet ${v(/SamsungBrowser\/(\d+)/)}`;
  else if (v(/Edg(?:A|iOS)?\/(\d+)/)) browser = `Edge ${v(/Edg(?:A|iOS)?\/(\d+)/)}`;
  else if (v(/OPR\/(\d+)/)) browser = `Opera ${v(/OPR\/(\d+)/)}`;
  else if (v(/CriOS\/(\d+)/)) browser = `Chrome ${v(/CriOS\/(\d+)/)}`;
  else if (v(/FxiOS\/(\d+)/) || v(/Firefox\/(\d+)/)) browser = `Firefox ${v(/FxiOS\/(\d+)/) || v(/Firefox\/(\d+)/)}`;
  else if (v(/Chrome\/(\d+)/)) browser = `Chrome ${v(/Chrome\/(\d+)/)}`;
  else if (/Safari\//.test(s)) browser = "Safari";

  const label = [device, os && os !== device ? os : "", browser].filter(Boolean).join(" · ") || "Unknown device";
  return { device, os, browser, label };
}

/** What the browser told us about its connection when the error happened (lib/utils/clientErrorReporter). */
export type NetDiagnostics = {
  online?: boolean;
  type?: string; // effectiveType: "4g" | "3g" | "2g" | "slow-2g"
  rtt?: number; // ms
  downlink?: number; // Mbit/s
  saveData?: boolean;
  visibility?: string; // "visible" | "hidden"
  /** Hidden in the last minute — a locked phone only reports once it wakes, so "visible" alone misleads. */
  backgroundRecently?: boolean;
  /** Lost the internet in the last minute. */
  offlineRecently?: boolean;
  connected?: boolean; // Convex websocket open at that moment
  reconnects?: number; // times this tab has had to reconnect
  retries?: number; // failed connection attempts
  secondsOnPage?: number;
};

/** The connection in one sentence: "Offline", "App in the background", "Slow 3g, 900 ms". */
export function networkSummary(n: NetDiagnostics | null | undefined): string | null {
  if (!n) return null;
  const parts: string[] = [];
  if (n.online === false) parts.push("Offline — no internet");
  else if (n.offlineRecently) parts.push("Lost the internet a moment before");
  if (n.visibility === "hidden" || n.backgroundRecently) parts.push("App in the background (screen locked or another app open)");
  if (n.type) {
    const slow = n.type === "2g" || n.type === "slow-2g" || n.type === "3g" || (n.rtt ?? 0) >= 600;
    parts.push(`${slow ? "Slow" : "Good"} connection (${n.type}${n.rtt ? `, ${n.rtt} ms` : ""}${n.downlink ? `, ${n.downlink} Mbit/s` : ""})`);
  }
  if (n.connected === false) parts.push("Not connected to our server at that moment");
  if ((n.reconnects ?? 0) > 3) parts.push(`Reconnected ${n.reconnects} times on this page`);
  return parts.length ? parts.join(" · ") : n.online ? "Online" : null;
}

/** What a dropped connection means — so nobody chases it as a bug. */
export function connectionDropMeaning(n: NetDiagnostics | null | undefined): { meaning: string; nextStep: string } {
  const background = n?.visibility === "hidden" || n?.backgroundRecently === true;
  const offline = n?.online === false || n?.offlineRecently === true;
  return {
    meaning: offline
      ? "The device had no internet when this was sent, so it never reached our server."
      : background
        ? "The phone locked its screen or switched apps while this was running, and the connection to our server closed."
        : "The connection to our server dropped while this was running — usually weak signal or a network switch. If many shops drop at the same minute, it was our server restarting (a deploy).",
    nextStep: "Not a fault in the app. Background refreshes retry by themselves; if the person saw an error, they only need to try again.",
  };
}

// ── Failed payments ────────────────────────────────────────────────────────

export type PaymentFault = "provider" | "payer" | "none";

export type PaymentFailureMeaning = {
  /** "Invalid merchant (code 03)" */
  reason: string;
  /** What actually happened, in plain words. */
  meaning: string;
  /** Who has to act, and how. */
  nextStep: string;
  /** provider = Flutterwave / our account (we must act); payer = the person paying; none = they cancelled. */
  fault: PaymentFault;
};

/** Why a payment failed, from Flutterwave's processor response or our own refusal text. */
export function paymentFailureMeaning(
  pr: { type?: string | null; code?: string | null } | null | undefined,
  errorText?: string | null,
): PaymentFailureMeaning {
  const type = String(pr?.type || "").toLowerCase();
  const code = pr?.code && pr.code !== "00" ? ` (code ${pr.code})` : "";
  const err = String(errorText || "").trim();
  const e = err.toLowerCase();

  if (/stopped this payment for a check|under review|irregular transaction/.test(e)) {
    return {
      reason: "Held by Flutterwave for a risk review",
      meaning: "Flutterwave is reviewing our account over \"irregular transaction patterns\" and refuses payments before the payer is even asked. Nothing was charged.",
      nextStep: "Ours to fix: contact Flutterwave support to clear the review. The shop should not retry until then.",
      fault: "provider",
    };
  }
  if (type === "invalid_merchant" || pr?.code === "03") {
    return {
      reason: `Invalid merchant${code}`,
      meaning: "The mobile money network refused Kuttos as the merchant for this charge — the payer did nothing wrong. Usually our Flutterwave account is not cleared to collect on that network, or it is under review.",
      nextStep: "Ours to fix: send the reference to Flutterwave support and ask why mobile money collections return \"invalid merchant\".",
      fault: "provider",
    };
  }
  if (type === "transaction_not_permitted_card" || pr?.code === "57") {
    return {
      reason: `Transaction not permitted${code}`,
      meaning: "The payer's mobile money wallet or card does not allow this payment — a limit, an unfinished KYC, or the network refused it.",
      nextStep: "The payer checks their wallet limits or KYC with their network, or pays with another number or card.",
      fault: "payer",
    };
  }
  if (type === "insufficient_funds" || pr?.code === "51") {
    return { reason: `Not enough money${code}`, meaning: "The payer's account did not have enough money.", nextStep: "The payer tops up and tries again.", fault: "payer" };
  }
  if (type === "do_not_honor" || pr?.code === "05") {
    return { reason: `Declined by the bank${code}`, meaning: "The card's bank declined it.", nextStep: "The payer calls their bank or uses another card.", fault: "payer" };
  }
  if (type === "expired_card" || type === "invalid_card" || type === "lost_card" || type === "stolen_card") {
    return { reason: `${type.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}${code}`, meaning: "The card cannot be charged.", nextStep: "The payer uses another card.", fault: "payer" };
  }
  if (type === "timeout" || /timed out|expired|did not approve|no response/.test(e)) {
    return { reason: "Not approved in time", meaning: "The payer did not approve the prompt on their phone before it expired.", nextStep: "Try again and approve the prompt straight away.", fault: "payer" };
  }
  if (type === "cancelled" || /cancel/.test(e)) {
    return { reason: "Cancelled", meaning: "The payer cancelled, or closed the payment window, before approving.", nextStep: "Nothing to fix.", fault: "none" };
  }
  if (type) {
    return { reason: `${type.replace(/_/g, " ")}${code}`, meaning: "Flutterwave declined the charge.", nextStep: "Look up the reference in the Flutterwave dashboard.", fault: "provider" };
  }
  return {
    reason: err ? err.slice(0, 120) : "No reason given",
    meaning: err ? "The payment was refused before it reached the payer's phone or card." : "Flutterwave marked it failed without a reason.",
    nextStep: "Look up the reference in the Flutterwave dashboard.",
    fault: "provider",
  };
}
